import React from "react";
import { PolylineF } from "@react-google-maps/api";
import { useThemes } from "../contexts/ThemeContext";
import useTheme from "../hooks/useTheme";

const CustomRoutePath = ({ points, threshold = 300, dashed }) => {
  const { theme } = useTheme();
  const { themes, currentTheme } = useThemes();
  const themeT = themes[currentTheme];
  const calculateDistance = (point1, point2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (point1.lat * Math.PI) / 180;
    const φ2 = (point2.lat * Math.PI) / 180;
    const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
    const Δλ = ((point2.lng - point2.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const calculateCurvedPath = (start, end, numPoints = 50) => {
    const curvedPath = [];

    // Calculate the midpoint between start and end
    const midPoint = {
      lat: (start.lat + end.lat) / 2,
      lng: (start.lng + end.lng) / 2,
    };

    // Calculate the distance between points
    const distance = Math.sqrt(
      Math.pow(end.lat - start.lat, 2) + Math.pow(end.lng - start.lng, 2),
    );

    // Adjust the curve height based on the distance
    const curveHeight = distance * 0.4;

    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;

      // Quadratic bezier curve formula
      const lat =
        Math.pow(1 - t, 2) * start.lat +
        2 * (1 - t) * t * (midPoint.lat + curveHeight) +
        Math.pow(t, 2) * end.lat;

      const lng =
        Math.pow(1 - t, 2) * start.lng +
        2 * (1 - t) * t * midPoint.lng +
        Math.pow(t, 2) * end.lng;

      curvedPath.push({ lat, lng });
    }

    return curvedPath;
  };

  const processPoints = () => {
    if (!points || points.length < 2) return [];
    const processedPath = [];
    let i = 0;

    while (i < points.length - 1) {
      const currentPoint = points[i];
      const nextPoint = points[i + 1];

      processedPath.push(currentPoint);

      const distance = calculateDistance(currentPoint, nextPoint);

      if (distance > threshold) {
        // Add Bezier curve points between these points
        const curvePoints = calculateCurvedPath(currentPoint, nextPoint);
        processedPath.push(...curvePoints);
      }

      i++;
    }

    // Add the last point
    processedPath.push(points[points.length - 1]);

    return processedPath;
  };

  const lineSymbol = {
    path: "M 0, -0.8, 0,0.8",
    strokeOpacity: 1,
    scale: 2,
  };

  return (
    points?.length > 0 &&
    (dashed ? (
      <PolylineF
        path={processPoints()}
        options={{
          strokeColor: theme === "light" ? "#455a64" : "#9fb0b8",
          //strokeWeight: 4.5,
          strokeOpacity: 0,
          zIndex: 10,
          icons: [
            {
              icon: lineSymbol,
              offset: "0",
              repeat: "7px",
            },
          ],
        }}
      />
    ) : (
      <PolylineF
        path={processPoints()}
        options={{
          strokeColor: theme === "light" ? "#455a64" : "#9fb0b8",
          strokeWeight: 4.5,
          strokeOpacity: 1,
          zIndex: 5,
          geodesic: true,
        }}
      />
    ))
  );
};

export default CustomRoutePath;
